<template>
  <section class="buffet-plan-picker">
    <header class="buffet-picker-header">
      <div class="buffet-picker-heading">
        <h2 class="buffet-picker-title">{{ $t("select_buffet_plan") }}</h2>
        <p class="buffet-picker-subtitle">{{ $t("buffet_select_plan") }}</p>
      </div>
    </header>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    />

    <div
      v-if="readonly"
      class="buffet-readonly-notice"
      role="status"
      :aria-label="readonlyNoticeText"
    >
      <v-icon size="18" class="buffet-readonly-notice__icon"
        >fal fa-info-circle</v-icon
      >
      <span class="buffet-readonly-notice__text">
        {{ readonlyNoticeText }}
      </span>
    </div>

    <div v-if="!loading && plans.length === 0" class="buffet-picker-empty">
      <v-icon size="32" class="buffet-picker-empty__icon">fal fa-utensils</v-icon>
      <p class="buffet-picker-empty__text">{{ $t("no_data") }}</p>
    </div>

    <div v-else class="buffet-plan-cards">
      <button
        v-for="p in plans"
        :key="p.id"
        type="button"
        class="buffet-plan-card"
        :aria-label="`${p.name} ${formatMoney(p.price)}`"
        @click="onSelect(p)"
      >
        <div class="buffet-plan-card__head">
          <span class="buffet-plan-card__name">{{ p.name }}</span>
          <span class="buffet-plan-card__price">
            {{ formatMoney(p.price) }} ₭
          </span>
        </div>

        <div class="buffet-plan-card__meta">
          <span class="buffet-plan-card__meta-label">
            {{ $t("categories_included") }}
          </span>
          <div
            v-if="p.included_categories && p.included_categories.length"
            class="buffet-plan-card__chips"
          >
            <span
              v-for="c in p.included_categories"
              :key="c.id"
              class="buffet-plan-card__chip"
            >
              {{ getCategoryName(c) }}
            </span>
          </div>
          <span
            v-else
            class="buffet-plan-card__chip buffet-plan-card__chip--empty"
          >
            {{ $t("no_categories_included") }}
          </span>
        </div>

        <div class="buffet-plan-card__cta">
          <span class="buffet-plan-card__cta-text">
            {{ $t("view_plan_items") }}
          </span>
          <v-icon size="14" class="buffet-plan-card__cta-icon">
            fal fa-chevron-right
          </v-icon>
        </div>
      </button>
    </div>
  </section>
</template>

<script>
import { myMixin } from "@/mixin/main";

export default {
  name: "BuffetPlanScanSheet",
  mixins: [myMixin],
  props: {
    plans: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    readonlyNotice: {
      type: String,
      default: "",
    },
  },
  computed: {
    readonlyNoticeText() {
      if (this.readonlyNotice) {
        return this.readonlyNotice;
      }
      return this.$t("please_ask_staff_to_open_table");
    },
  },
  methods: {
    formatMoney(n) {
      return this.$options.filters.NumberFormat(Number(n || 0));
    },
    onSelect(plan) {
      this.$emit("select", plan);
    },
  },
};
</script>

<style scoped>
.buffet-plan-picker {
  padding-top: 4px;
  margin-top: 20px;
}

.buffet-picker-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.buffet-picker-heading {
  min-width: 0;
}

.buffet-picker-title {
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.3px;
  color: var(--qr-text);
  margin: 0;
  line-height: 1.2;
}

.buffet-picker-subtitle {
  font-size: 12.5px;
  color: var(--qr-muted);
  margin: 4px 0 0;
  line-height: 1.3;
}

.buffet-plan-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.buffet-plan-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border-radius: 18px;
  background: var(--qr-card-bg);
  border: 0.5px solid var(--qr-card-border);
  box-shadow: 0 6px 18px rgba(40, 28, 16, 0.06),
    0 1px 0 rgba(255, 255, 255, 0.8) inset;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  color: var(--qr-text);
  min-height: 88px;
  touch-action: manipulation;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
  width: 100%;
}

.buffet-plan-card:hover {
  border-color: var(--qr-accent-strong-border);
  box-shadow: 0 10px 28px rgba(40, 28, 16, 0.1);
}

.buffet-plan-card:focus-visible {
  outline: 2px solid var(--qr-accent);
  outline-offset: 2px;
}

.buffet-plan-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.buffet-plan-card__name {
  font-weight: 800;
  font-size: 15px;
  color: var(--qr-text);
  line-height: 1.25;
  letter-spacing: -0.2px;
}

.buffet-plan-card__price {
  font-weight: 800;
  font-size: 15px;
  color: var(--qr-accent);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.2px;
}

.buffet-plan-card__meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.buffet-plan-card__meta-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--qr-muted);
}

.buffet-plan-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.buffet-plan-card__chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  background: var(--qr-accent-faint);
  color: var(--qr-accent);
  border: 0.5px solid var(--qr-accent-border);
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.buffet-plan-card__chip--empty {
  background: var(--qr-subtle-bg);
  color: var(--qr-muted);
  border-color: var(--qr-card-border);
}

.buffet-plan-card__cta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: auto;
  color: var(--qr-accent);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.1px;
}

.buffet-plan-card__cta-icon {
  color: var(--qr-accent) !important;
}

.buffet-picker-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 16px;
  color: var(--qr-muted);
}

.buffet-picker-empty__icon {
  color: var(--qr-muted) !important;
}

.buffet-picker-empty__text {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
}

.buffet-readonly-notice {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  margin-bottom: 12px;
  border-radius: 14px;
  background: var(--qr-accent-faint);
  border: 0.5px solid var(--qr-accent-border);
  color: var(--qr-text);
}

.buffet-readonly-notice__icon {
  color: var(--qr-accent);
  flex-shrink: 0;
}

.buffet-readonly-notice__text {
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.35;
}

@media (prefers-reduced-motion: reduce) {
  .buffet-plan-card {
    transition: none !important;
  }
}
</style>
